import { ColliClient } from '@frontend/colli/api';
import { Application, Feature } from '@frontend/functionality/types';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageStateName, PackageTriggerName, PackageType } from '@frontend/package/types';
import { TransactionClient, TransactionUserClient } from '@frontend/transaction/api';
import { TransactionStateName, TransactionUserType } from '@frontend/transaction/types';

import { isMovableTransactionState } from '../../hooks/useMoveTransaction/move-transaction';
import { BulkMoveAssignment, BulkMoveDestination } from './bulk-move-colli-units';
import { findActivePackagesByType } from './bulk-package-helpers';

export interface BulkMoveTransactionInput {
    accountId: string;
    transactionId: string;
    transactionState: TransactionStateName;
    assignments: Array<{ colliId: string | null; destination: BulkMoveDestination }>;
    dropOffPackageWorkflowId: string;
    pickUpPackageWorkflowId: string;
    removePackageWorkflowId?: string;
    actorUserId?: string;
    triggerLocationSpotId?: string;
}

export interface BulkMoveTransactionSuccess {
    transactionId: string;
    dropPackages: Package[];
    pickPackages: Package[];
    removalPackages: Package[];
    /** Source pick packages skipped (SLOT may open). */
    sourceOpeningPackages: Package[];
}

export interface BulkMoveTransactionFailure {
    transactionId: string;
    error: unknown;
}

export interface BulkMoveTransactionsResult {
    successes: BulkMoveTransactionSuccess[];
    failures: BulkMoveTransactionFailure[];
    sourceOpeningPackages: Package[];
    removalPackages: Package[];
    dropPackages: Package[];
}

const matchPickForColli = (picks: Package[], colliId: string | null): Package | undefined => {
    if (colliId) {
        return picks.find((pkg) => pkg.colli_id === colliId) ?? picks[0];
    }
    return picks[0];
};

const postDropPickChain = async (args: {
    accountId: string;
    transactionId: string;
    destination: BulkMoveDestination;
    colliId: string | null;
    previousPackageId?: string;
    dropOffPackageWorkflowId: string;
    pickUpPackageWorkflowId: string;
    actorUserId?: string;
}): Promise<{ dropPackage: Package; pickPackage: Package }> => {
    const shared = {
        spot_id: args.destination.spotId,
        module_id: args.destination.moduleId,
        slot_id: args.destination.slotId,
        quantity: 1,
        state: PackageStateName.PACKAGE_CREATED,
        application: Application.PUDO,
        feature: Feature.BULK_MOVE_TRANSACTION,
        user_id: args.actorUserId ?? null,
        blocking: true,
        colli_id: args.colliId
    };

    const dropPackage = await PackageClient.postPackage(args.accountId, args.transactionId, {
        ...shared,
        type: PackageType.DROP_OFF,
        workflow_id: args.dropOffPackageWorkflowId,
        dependent_package_id: args.previousPackageId,
        priority: 4
    });

    const pickPackage = await PackageClient.postPackage(args.accountId, args.transactionId, {
        ...shared,
        type: PackageType.PICK_UP,
        workflow_id: args.pickUpPackageWorkflowId,
        dependent_package_id: dropPackage.id,
        priority: 3
    });

    return { dropPackage, pickPackage };
};

/**
 * Moves one transaction's colli assignments.
 * Pick-up pending: skip pick → REMOVAL → new drop/pick per colli.
 * Drop-off pending: skip old drop/pick → new drop/pick only (no REMOVAL).
 */
export async function moveBulkTransaction(input: BulkMoveTransactionInput): Promise<BulkMoveTransactionSuccess> {
    const {
        accountId,
        transactionId,
        transactionState,
        assignments,
        dropOffPackageWorkflowId,
        pickUpPackageWorkflowId,
        removePackageWorkflowId,
        actorUserId,
        triggerLocationSpotId
    } = input;

    if (!isMovableTransactionState(transactionState)) {
        throw new Error('Transaction is not in a movable state');
    }
    if (assignments.length === 0) {
        throw new Error('No destination assignments provided');
    }

    const response = await PackageClient.fetchAccountTransactionPackages(accountId, transactionId);
    const packages = response.results ?? [];
    const triggerMeta = { location: triggerLocationSpotId, actor: actorUserId };

    const dropPackages: Package[] = [];
    const pickPackages: Package[] = [];
    const removalPackages: Package[] = [];
    const sourceOpeningPackages: Package[] = [];

    if (transactionState === TransactionStateName.TRANSACTION_DROP_OFF_PENDING) {
        for (const pkg of [...findActivePackagesByType(packages, PackageType.DROP_OFF), ...findActivePackagesByType(packages, PackageType.PICK_UP)]) {
            await PackageWorkflowClient.triggerPackageState(accountId, transactionId, pkg.id, {
                trigger: PackageTriggerName.PACKAGE_SKIP,
                ...triggerMeta
            });
        }

        let previousPackageId: string | undefined;
        for (const assignment of assignments) {
            const chain = await postDropPickChain({
                accountId,
                transactionId,
                destination: assignment.destination,
                colliId: assignment.colliId,
                previousPackageId,
                dropOffPackageWorkflowId,
                pickUpPackageWorkflowId,
                actorUserId
            });
            dropPackages.push(chain.dropPackage);
            pickPackages.push(chain.pickPackage);
            previousPackageId = chain.pickPackage.id;

            if (assignment.colliId) {
                // Planned destination only — current_* stays source until physically moved.
                await ColliClient.patchColli(accountId, transactionId, assignment.colliId, {
                    spot_id: assignment.destination.spotId,
                    module_id: assignment.destination.moduleId,
                    slot_id: assignment.destination.slotId
                });
            }
        }
    } else {
        if (!removePackageWorkflowId) {
            throw new Error('Remove package workflow is required for pick-up pending moves');
        }

        const activePicks = findActivePackagesByType(packages, PackageType.PICK_UP);
        if (activePicks.length === 0) {
            throw new Error('No pending pick-up package found to move');
        }

        if (actorUserId) {
            await TransactionUserClient.postTransactionUser(accountId, transactionId, {
                user_id: actorUserId,
                type: TransactionUserType.REMOVER
            });
        }

        for (const assignment of assignments) {
            const pendingPick = matchPickForColli(activePicks, assignment.colliId);
            if (!pendingPick) {
                throw new Error('No pending pick-up package found for colli assignment');
            }

            await PackageWorkflowClient.triggerPackageState(accountId, transactionId, pendingPick.id, {
                trigger: PackageTriggerName.PACKAGE_SKIP,
                ...triggerMeta
            });
            if (pendingPick.slot_id) {
                sourceOpeningPackages.push(pendingPick);
            }

            const removal = await PackageClient.postPackage(accountId, transactionId, {
                iot_id: pendingPick.iot_id,
                product_id: pendingPick.product_id,
                equipment_id: pendingPick.equipment_id,
                colli_id: pendingPick.colli_id ?? assignment.colliId,
                spot_id: pendingPick.spot_id ?? undefined,
                module_id: pendingPick.module_id ?? undefined,
                slot_id: pendingPick.slot_id ?? undefined,
                quantity: pendingPick.quantity,
                application: Application.PUDO,
                feature: Feature.BULK_MOVE_TRANSACTION,
                priority: pendingPick.priority,
                budget_id: pendingPick.budget_id,
                cost_center_id: pendingPick.cost_center_id,
                blocking: pendingPick.blocking,
                dependent_package_id: pendingPick.id,
                workflow_id: removePackageWorkflowId,
                user_id: actorUserId ?? pendingPick.user_id,
                type: PackageType.REMOVAL
            });
            removalPackages.push({
                ...removal,
                transaction_id: removal.transaction_id ?? transactionId,
                slot_id: removal.slot_id ?? pendingPick.slot_id
            });

            const chain = await postDropPickChain({
                accountId,
                transactionId,
                destination: assignment.destination,
                colliId: assignment.colliId ?? pendingPick.colli_id,
                previousPackageId: removal.id,
                dropOffPackageWorkflowId,
                pickUpPackageWorkflowId,
                actorUserId
            });
            dropPackages.push(chain.dropPackage);
            pickPackages.push(chain.pickPackage);

            if (assignment.colliId) {
                // Planned destination only — current_* stays source until physically moved.
                await ColliClient.patchColli(accountId, transactionId, assignment.colliId, {
                    spot_id: assignment.destination.spotId,
                    module_id: assignment.destination.moduleId,
                    slot_id: assignment.destination.slotId
                });
            }
        }
    }

    const primaryDestination = assignments[0].destination;
    await TransactionClient.patchTransaction(accountId, transactionId, {
        spot_id: primaryDestination.spotId,
        slot_id: primaryDestination.slotId
    });

    return { transactionId, dropPackages, pickPackages, removalPackages, sourceOpeningPackages };
}

/** Best-effort bulk move across transactions. */
export async function moveBulkTransactions(inputs: BulkMoveTransactionInput[]): Promise<BulkMoveTransactionsResult> {
    const successes: BulkMoveTransactionSuccess[] = [];
    const failures: BulkMoveTransactionFailure[] = [];
    const sourceOpeningPackages: Package[] = [];
    const removalPackages: Package[] = [];
    const dropPackages: Package[] = [];

    for (const input of inputs) {
        try {
            const result = await moveBulkTransaction(input);
            successes.push(result);
            sourceOpeningPackages.push(...result.sourceOpeningPackages);
            removalPackages.push(...result.removalPackages);
            dropPackages.push(...result.dropPackages);
        } catch (error) {
            failures.push({ transactionId: input.transactionId, error });
        }
    }

    return { successes, failures, sourceOpeningPackages, removalPackages, dropPackages };
}

export const groupAssignmentsByTransaction = (assignments: BulkMoveAssignment[]): Map<string, BulkMoveAssignment[]> => {
    const map = new Map<string, BulkMoveAssignment[]>();
    for (const assignment of assignments) {
        const list = map.get(assignment.transactionId) ?? [];
        list.push(assignment);
        map.set(assignment.transactionId, list);
    }
    return map;
};

/** Keep one package per SLOT for door UI (MQTT/status keyed by slot_id). */
export const dedupePackagesBySlotId = (packages: Package[]): Package[] => {
    const seenSlotIds = new Set<string>();
    const result: Package[] = [];

    for (const pkg of packages) {
        const slotId = pkg.slot_id;
        if (!slotId) {
            result.push(pkg);
            continue;
        }
        if (seenSlotIds.has(slotId)) continue;
        seenSlotIds.add(slotId);
        result.push(pkg);
    }

    return result;
};
